<template>
  <div class="activity-log-page pa-10">

    <!-- Header -->
    <div class="d-flex flex-wrap justify-space-between align-center mb-7 gap-3">
      <div>
        <div class="dash-page-title">Activity Log</div>
        <div class="dash-page-sub mt-1">System-wide record of actions performed</div>
      </div>
    </div>

    <!-- Filters -->
    <div class="filter-bar rounded-xl pa-5 mb-6">
      <v-row align="center" style="gap:0;">
        <v-col cols="12" sm="3">
          <v-text-field
            v-model="filters.date_from"
            label="From Date"
            type="date"
            density="compact"
            variant="outlined"
            hide-details
            clearable
          />
        </v-col>
        <v-col cols="12" sm="3">
          <v-text-field
            v-model="filters.date_to"
            label="To Date"
            type="date"
            density="compact"
            variant="outlined"
            hide-details
            clearable
          />
        </v-col>
        <v-col cols="12" sm="3">
          <v-select
            v-model="filters.action"
            :items="availableActions"
            item-title="label"
            item-value="value"
            label="Action Type"
            density="compact"
            variant="outlined"
            hide-details
            clearable
          />
        </v-col>
        <v-col cols="12" sm="3" class="d-flex gap-2">
          <v-btn
            color="#10b981"
            variant="flat"
            class="text-white"
            rounded="lg"
            block
            @click="fetchLogs"
          >
            <v-icon start>mdi-magnify</v-icon>
            Filter
          </v-btn>
        </v-col>
      </v-row>
    </div>

    <!-- Log Table -->
    <div class="dash-card rounded-xl pa-5">
      <div class="d-flex justify-space-between align-center mb-4">
        <div>
          <div class="card-title">Activity Records</div>
          <div class="card-sub">{{ pagination.total }} total entries</div>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="d-flex justify-center align-center py-12">
        <v-progress-circular indeterminate color="#10b981" size="40" />
      </div>

      <!-- Empty -->
      <div v-else-if="!logs.length" class="text-center py-12">
        <v-icon size="48" color="#d1d5db">mdi-history</v-icon>
        <div class="mt-3" style="color:#9ca3af; font-size:14px;">No activity records found</div>
      </div>

      <!-- Table -->
      <div v-else>
        <div
          v-for="log in logs"
          :key="log.id"
          class="log-row d-flex align-start"
        >
          <!-- Action badge -->
          <div
            class="action-badge rounded-lg d-flex align-center"
            :style="getActionStyle(log.action)"
          >
            <v-icon size="16" :color="getActionColor(log.action)">{{ getActionIcon(log.action) }}</v-icon>
          </div>

          <!-- Content -->
          <div class="log-content flex-grow-1">
            <div class="d-flex flex-wrap align-center justify-space-between" style="gap:8px;">
              <div>
                <span class="action-chip rounded-pill px-2 py-1 mr-2" :style="getChipStyle(log.action)">
                  {{ getActionLabel(log.action) }}
                </span>
                <span class="log-desc">{{ log.description || log.action }}</span>
              </div>
              <span class="log-time">{{ formatDateTime(log.created_at) }}</span>
            </div>
            <div class="log-meta mt-1">
              <v-icon size="12" color="#9ca3af">mdi-account-outline</v-icon>
              <span class="ml-1">{{ log.performed_by_name || 'Unknown' }}</span>
              <span v-if="log.table_from" class="ml-3">
                <v-icon size="12" color="#9ca3af">mdi-table</v-icon>
                <span class="ml-1">{{ formatTableName(log.table_from) }}</span>
              </span>
            </div>
          </div>
        </div>

        <!-- Pagination -->
        <div class="d-flex justify-center align-center mt-6" style="gap:12px;">
          <v-btn
            variant="text"
            size="small"
            :disabled="pagination.page <= 1"
            @click="goToPage(pagination.page - 1)"
          >
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
          <span class="page-info">Page {{ pagination.page }} of {{ totalPages }}</span>
          <v-btn
            variant="text"
            size="small"
            :disabled="pagination.page >= totalPages"
            @click="goToPage(pagination.page + 1)"
          >
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
const ACTION_CONFIG = {
  USER_LOGIN:               { color: '#059669', bg: '#ecfdf5', border: '#a7f3d0', icon: 'mdi-login-variant',            label: 'Login' },
  USER_LOGOUT:              { color: '#6b7280', bg: '#f9fafb', border: '#e5e7eb', icon: 'mdi-logout-variant',           label: 'Logout' },
  BATCH_CREATED:            { color: '#059669', bg: '#ecfdf5', border: '#a7f3d0', icon: 'mdi-layers-plus',              label: 'Batch Created' },
  STUDENT_ENDORSED:         { color: '#7c3aed', bg: '#f5f3ff', border: '#ddd6fe', icon: 'mdi-send-outline',             label: 'Student Endorsed' },
  STUDENT_DEPLOYED:         { color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe', icon: 'mdi-check-circle-outline',     label: 'Student Deployed' },
  STUDENT_PULLED_OUT:       { color: '#dc2626', bg: '#fef2f2', border: '#fecaca', icon: 'mdi-account-arrow-left-outline', label: 'Student Pulled Out' },
  STUDENT_REASSIGNED:       { color: '#d97706', bg: '#fffbeb', border: '#fde68a', icon: 'mdi-account-switch-outline',   label: 'Student Reassigned' },
  REQUIREMENT_SUBMITTED:    { color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe', icon: 'mdi-file-upload-outline',      label: 'Requirement Submitted' },
  REQUIREMENT_APPROVED:     { color: '#059669', bg: '#ecfdf5', border: '#a7f3d0', icon: 'mdi-file-check-outline',       label: 'Requirement Approved' },
  REQUIREMENT_DISAPPROVED:  { color: '#dc2626', bg: '#fef2f2', border: '#fecaca', icon: 'mdi-file-cancel-outline',      label: 'Requirement Disapproved' },
  ATTENDANCE_SCANNED:       { color: '#0d9488', bg: '#f0fdfa', border: '#99f6e4', icon: 'mdi-qrcode-scan',              label: 'Attendance Scanned' },
  STUDENT_EVALUATED:        { color: '#7c3aed', bg: '#f5f3ff', border: '#ddd6fe', icon: 'mdi-star-check-outline',        label: 'Student Evaluated' },
  INTERN_REQUEST_GRANTED:   { color: '#059669', bg: '#ecfdf5', border: '#a7f3d0', icon: 'mdi-handshake-outline',         label: 'Intern Request Granted' },
  MOA_UPLOADED:             { color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe', icon: 'mdi-file-sign',                 label: 'MOA Uploaded' },
  CORETIME_SUBMITTED:       { color: '#d97706', bg: '#fffbeb', border: '#fde68a', icon: 'mdi-calendar-clock-outline',    label: 'Coretime Submitted' },
  CORETIME_APPROVED:        { color: '#059669', bg: '#ecfdf5', border: '#a7f3d0', icon: 'mdi-calendar-check-outline',    label: 'Coretime Approved' },
  USER_ROLE_ASSIGNED:       { color: '#6b7280', bg: '#f9fafb', border: '#e5e7eb', icon: 'mdi-account-cog-outline',       label: 'Role Assigned' },
  STUDENT_PROFILING_UPDATED:{ color: '#7c3aed', bg: '#f5f3ff', border: '#ddd6fe', icon: 'mdi-account-edit-outline',      label: 'Profiling Updated' },
  INTERN_FEEDBACK_ADDED:    { color: '#d97706', bg: '#fffbeb', border: '#fde68a', icon: 'mdi-message-alert-outline',     label: 'Intern Feedback Added' },
};

const DEFAULT_CONFIG = { color: '#6b7280', bg: '#f9fafb', border: '#e5e7eb', icon: 'mdi-history', label: '' };

export default {
  name: 'ActivityLogPage',
  data() {
    return {
      logs: [],
      loading: false,
      filters: {
        date_from: '',
        date_to: '',
        action: '',
      },
      pagination: {
        page: 1,
        limit: 20,
        total: 0,
      },
      availableActions: Object.entries(ACTION_CONFIG).map(([value, cfg]) => ({
        value,
        label: cfg.label,
      })),
    };
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.pagination.total / this.pagination.limit));
    },
  },
  mounted() {
    this.fetchLogs();
  },
  methods: {
    async fetchLogs() {
      this.loading = true;
      try {
        const params = new URLSearchParams({
          page: this.pagination.page,
          limit: this.pagination.limit,
        });
        if (this.filters.date_from) params.append('date_from', this.filters.date_from);
        if (this.filters.date_to) params.append('date_to', this.filters.date_to);
        if (this.filters.action) params.append('action', this.filters.action);

        const res = await this.axiosCall(`/system-logs?${params.toString()}`, 'GET');
        if (res?.data) {
          this.logs = res.data.data ?? [];
          this.pagination.total = res.data.total ?? 0;
          this.pagination.page = res.data.page ?? 1;
        }
      } catch (e) {
        console.error('Failed to load activity logs', e);
      } finally {
        this.loading = false;
      }
    },

    goToPage(page) {
      this.pagination.page = page;
      this.fetchLogs();
    },

    getConfig(action) {
      return ACTION_CONFIG[action] ?? DEFAULT_CONFIG;
    },
    getActionColor(action) {
      return this.getConfig(action).color;
    },
    getActionIcon(action) {
      return this.getConfig(action).icon;
    },
    getActionLabel(action) {
      return this.getConfig(action).label || action;
    },
    getActionStyle(action) {
      const cfg = this.getConfig(action);
      return `background:${cfg.bg}; border:1px solid ${cfg.border}; min-width:36px; height:36px; padding:8px; flex-shrink:0; margin-right:14px; margin-top:2px;`;
    },
    getChipStyle(action) {
      const cfg = this.getConfig(action);
      return `background:${cfg.bg}; border:1px solid ${cfg.border}; color:${cfg.color}; font-size:11px; font-weight:600;`;
    },

    formatDateTime(val) {
      if (!val) return '';
      const d = new Date(val);
      return d.toLocaleString('en-PH', {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    },

    formatTableName(name) {
      return name ? name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '';
    },
  },
};
</script>

<style scoped>
.activity-log-page {
  width: 100%;
}

.dash-page-title {
  font-size: 30px;
  font-weight: 800;
  color: #111827;
  letter-spacing: -0.5px;
}

.dash-page-sub {
  font-size: 13px;
  color: #9ca3af;
  font-weight: 400;
}

.filter-bar {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 6px rgba(0,0,0,0.06);
}

.dash-card {
  background: #ffffff;
  border: 1px solid #d1d5db;
  box-shadow: 0 1px 6px rgba(0,0,0,0.06);
}

.card-title {
  font-size: 15px;
  font-weight: 700;
  color: #111827;
}

.card-sub {
  font-size: 12.5px;
  color: #9ca3af;
}

.log-row {
  padding: 14px 0;
  border-bottom: 1px solid #f3f4f6;
}
.log-row:last-child {
  border-bottom: none;
}

.action-badge {
  justify-content: center;
}

.log-desc {
  font-size: 13px;
  color: #111827;
  font-weight: 500;
}

.log-meta {
  font-size: 11.5px;
  color: #9ca3af;
  display: flex;
  align-items: center;
}

.log-time {
  font-size: 11.5px;
  color: #9ca3af;
  white-space: nowrap;
  flex-shrink: 0;
}

.action-chip {
  display: inline-block;
  line-height: 1.4;
}

.page-info {
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;
}

.gap-2 {
  gap: 8px;
}
</style>
